import React from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, IconButton, Stack, Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FlagImport from 'react-world-flags';
import { useTranslation } from 'react-i18next';
import { useSupportedLanguages } from '../../../store/selectors/loginMetadataSelector';
import { FlagFallback, FlagLabel, LangInputWrapper } from './Translation.styles';
import { useTranslations } from '../../../hooks/UseTranslations';
import { GeneralButton } from '../../../common/ui/GeneralButton';

// react-world-flags ships a UMD/CJS bundle. Under Vite the default import resolves
// to the module object ({ default: Flag }) rather than the component itself, so we
// unwrap the nested default when present.
const Flag = ((FlagImport as unknown as { default?: typeof FlagImport }).default ??
    FlagImport) as typeof FlagImport;

interface TranslationEditModalProps {
    open: boolean;
    onClose: () => void;
    entityType: string;
    entityId: string;
    property: string;
    nestedId?: string[];
    title?: string;
}

export const TranslationEditModal: React.FC<TranslationEditModalProps> = ({
    open,
    onClose,
    entityType,
    entityId,
    property,
    nestedId,
    title
}) => {
    const { t } = useTranslation();
    const supportedLanguages = useSupportedLanguages();
    const {
        translations,
        hasChanges,
        setTranslation,
        handleSave,
        flagError,
        handleFlagError
    } = useTranslations({
        open,
        entityType,
        entityId,
        property,
        nestedId,
        onClose
    });

    const getFlagCode = (lang: string): string => {
        switch (lang.toLowerCase()) {
            case 'en':
                return 'gb';
            case 'el':
                return 'gr';
            case 'cs':
                return 'cz';
            case 'uk':
                return 'ua';
            case 'he':
                return 'il';
            case 'zh':
                return 'cn';
            case 'fa':
                return 'ir';
            case 'sr':
                return 'rs';
            case 'ar':
                return 'sa';
            case 'da':
                return 'dk';
            case 'sv':
                return 'se';
            case 'et':
                return 'ee';
            case 'sl':
                return 'si';
            default:
                return lang;
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {title ?? t("label.editTranslations")}
                <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    {supportedLanguages.map((lang) => (
                        <LangInputWrapper key={lang}>
                            <Tooltip title={lang.toUpperCase()}>
                                {flagError[lang] ? (
                                    <FlagFallback>
                                        <FlagLabel variant="caption">?</FlagLabel>
                                    </FlagFallback>
                                ) : (
                                    <Flag
                                        code={getFlagCode(lang)}
                                        style={{ width: 32, height: 20 }}
                                        onError={() => handleFlagError(lang)}
                                    />
                                )}
                            </Tooltip>
                            <TextField
                                fullWidth
                                label={t(`languages.${lang}`)}
                                value={translations[lang] || ""}
                                onChange={(e) => setTranslation(lang, e.target.value)}
                            />
                        </LangInputWrapper>
                    ))}
                </Stack>
            </DialogContent>

            <DialogActions>
                <GeneralButton
                    appVariant="secondary"
                    onClick={onClose}
                >
                    {t("buttons.cancel")}
                </GeneralButton>

                <GeneralButton
                    onClick={handleSave}
                    disabled={!hasChanges}
                >
                    {t("buttons.save")}
                </GeneralButton>
            </DialogActions>
        </Dialog>
    );
};
